import type { ComputedRef } from 'vue';
import type { Composer } from 'vue-i18n';

import type { ColumnConfig, MetaEnrichedRow } from '@/components/datatable/dataTableTypes';
import { DataWithMeta } from '@/components/datatable/dataTableTypes';
import { processEnumField } from '@/utils/combined-fields-helper';
import { formatDate, isDateString } from '@/utils/dateUtils';
import { deepValueToString } from '@/utils/object';

type AnyRow = Record<string, any>;

/**
 * Options required to initialize the DataTable meta enrichment pipeline.
 *
 * This composable is intentionally state-less and derives all behavior
 * from the provided options.
 */
export type UseDataTableMetaRowsOptions = {
  /** Reactive list of all configured columns (visible + technical) */
  allColumns: ComputedRef<ColumnConfig[]>;

  /** Unique table identifier, used for expansion and row identity */
  dataKey: ComputedRef<string>;

  /** Store handling per-column expanded/collapsed state */
  expandedColumnsStore: {
    isColumnExpanded: (dataKey: string, colField: string) => boolean;
  };

  /** i18n translation function */
  t: Composer['t'];

  /** Current locale (used for formatting and translations) */
  locale: ComputedRef<string>;
};

/**
 * Removes HTML tags and normalizes whitespace.
 *
 * Used mainly to derive stable, human-readable search strings
 * from rendered HTML content.
 */
function stripHtmlTags(input: string): string {
  return input
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalizes arrays for search purposes.
 *
 * - Objects are stringified deeply
 * - Enums are translated
 * - Values are concatenated with a delimiter
 */
function normalizeMetaArray(
  t: Composer['t'],
  arr: unknown[] | null | undefined,
  searchKey?: string,
): string {
  if (!Array.isArray(arr) || arr.length === 0) return '';

  return arr
    .map((item) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        if (searchKey && Object.prototype.hasOwnProperty.call(obj, searchKey)) {
          return deepValueToString(obj[searchKey]);
        }
        return deepValueToString(obj);
      }

      const s = deepValueToString(item);
      return t(`enums.${String(s).toUpperCase()}`);
    })
    .filter(Boolean)
    .join(' | ');
}

/**
 * Builds a normalized, lowercased search string from a cell's meta information.
 *
 * This is the canonical place where search behavior is defined.
 * DataTable.vue should never reimplement this logic.
 */
function buildSearchString(t: Composer['t'], meta: any, searchKey?: string): string {
  if (meta.textValue) {
    return String(meta.textValue).toLowerCase();
  }

  if (meta.type === 'date') {
    return `${String(meta.renderedValue).toLowerCase()} | ${String(meta.rawvalue)}`;
  }

  if (meta.type === 'dateArray') {
    const rawValues = Array.isArray(meta.rawvalue) ? meta.rawvalue : [meta.rawvalue];
    const renderedValues = Array.isArray(meta.renderedValue)
      ? meta.renderedValue
      : [meta.renderedValue];

    // Combine each raw ISO date with its formatted counterpart
    const searchParts: string[] = [];
    rawValues.forEach((rawDate: string, index: number) => {
      if (rawDate) {
        searchParts.push(rawDate); // ISO string
        if (renderedValues[index]) {
          searchParts.push(String(renderedValues[index]).toLowerCase()); // formatted date
        }
      }
    });

    return searchParts.join(' | ');
  }

  if (meta.type === 'stringArray') {
    const values = Array.isArray(meta.rawvalue) ? meta.rawvalue : [meta.rawvalue];
    return values
      .filter(Boolean)
      .map((v: any) => String(v).toLowerCase())
      .join(' | ');
  }

  if (meta.type === 'array') {
    return normalizeMetaArray(t, meta.rawvalue, searchKey);
  }

  if (meta.type === 'object' && typeof meta.rawvalue === 'object' && meta.rawvalue !== null) {
    const obj = meta.rawvalue as Record<string, any>;

    if (searchKey && Object.prototype.hasOwnProperty.call(obj, searchKey)) {
      return String(obj[searchKey]).toLowerCase();
    }

    return Object.entries(obj)
      .map(([key, val]) => `${key}: ${String(val)}`)
      .join(' | ')
      .toLowerCase();
  }

  if (meta.type === 'string') {
    const renderedValue = String(meta.isExpanded ? meta.renderedValue : meta.fullValue)
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();

    const rawValue = meta.rawvalue
      ? String(meta.rawvalue).toLowerCase().replace(/\s+/g, '').trim()
      : undefined;

    return rawValue && rawValue !== renderedValue
      ? `${renderedValue} | ${rawValue}`
      : renderedValue;
  }

  return String(meta.renderedValue).toLowerCase();
}

/**
 * Renders a cell value based on column configuration.
 *
 * This is used during meta enrichment to derive:
 * - renderedValue
 * - fullValue
 * - truncatedValue
 *
 * NOTE: Rendering here is purely string-based and independent
 * from actual Vue rendering.
 */
function renderTableCellValue(
  locale: string,
  t: Composer['t'],
  value: any,
  col: ColumnConfig,
  row?: AnyRow,
  combined?: boolean,
): string | string[] {
  // Handle formatter for single values
  if (col.formatter && value != null && !Array.isArray(value)) {
    return col.formatter(String(value), { locale, row, column: col });
  }

  // Handle formatter for array values - apply formatter to each element
  if (col.formatter && Array.isArray(value)) {
    return value
      .filter((v) => v != null)
      .map((v) => col.formatter!(String(v), { locale, row, column: col }));
  }

  if (col.type === 'enum' && value) {
    return processEnumField(value) || value;
  }

  if (
    col.type === 'date' ||
    col.type === 'datetime' ||
    col.type === 'time' ||
    (!col.type && typeof value === 'string' && isDateString(value))
  ) {
    return value ? formatDate(value, locale, col.type) : '';
  }

  if (col.type === 'dateArray') {
    const values = Array.isArray(value) ? value : [value];
    return values
      .filter(Boolean)
      .map((dateValue) => formatDate(dateValue, locale, 'date'))
      .filter(Boolean);
  }

  if (col.type === 'stringArray') {
    return Array.isArray(value) ? value : [value];
  }

  if (col.type === 'boolean' && combined) {
    const iconTrueRaw = (col as any).iconTrue;
    const iconFalseRaw = (col as any).iconFalse;
    const iconTrue = typeof iconTrueRaw === 'function' ? iconTrueRaw(undefined) : iconTrueRaw;
    const iconFalse = typeof iconFalseRaw === 'function' ? iconFalseRaw(undefined) : iconFalseRaw;
    const cls = !!value ? (iconTrue ?? 'pi pi-check') : (iconFalse ?? 'pi pi-times');
    return `<i class="${String(cls)}"></i>`;
  }

  return value ?? '';
}

/**
 * Composable responsible for:
 *
 * - Enriching raw rows with DataWithMeta instances
 * - Providing canonical helpers for:
 *   - row identity
 *   - raw row access
 *   - search string extraction
 *   - comparable values for sorting
 *
 * This composable is the **single source of truth**
 * for all meta-related behavior in the DataTable.
 */
export function useDataTableMetaRows(opts: UseDataTableMetaRowsOptions) {
  const { allColumns, dataKey, expandedColumnsStore, t, locale } = opts;

  /**
   * Returns the stable row identifier used throughout the DataTable.
   */
  function getRowId(row: any): string | number | null {
    return row?.__rowKey ?? row?.id?.meta?.('rawvalue') ?? row?.id ?? null;
  }

  /**
   * Converts a meta-enriched row back into a plain DTO.
   *
   * Used before emitting data to the outside world
   * (e.g. save, edit, export).
   */
  function unwrapRow(row: any) {
    if (!row || typeof row !== 'object') return row;

    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(row)) {
      if (value instanceof DataWithMeta) {
        result[key] = value.meta('rawvalue');
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  /**
   * Enriches raw rows with DataWithMeta instances per column.
   *
   * This is the core of the table data pipeline:
   * raw → enriched → filtered → sorted → paginated
   */
  function enrichRows<T extends AnyRow>(data: T[]): MetaEnrichedRow<T>[] {
    const columnConfigMap = new Map<string, ColumnConfig>(
      (allColumns.value ?? []).map((col) => [col.field, col]),
    );

    return (data ?? []).map((row, rowIndex) => {
      const enrichedItem: Record<string, any> = {};

      columnConfigMap.forEach((colConfig, key) => {
        const type = colConfig?.type ?? 'string';
        const truncateLength = colConfig?.truncateLength || 50;
        let value: any = row[key as keyof T];

        if (value instanceof DataWithMeta) {
          enrichedItem[key] = value;
          return;
        }

        const metaValue: DataWithMeta = new DataWithMeta(value);

        if (colConfig?.combinedField?.length) {
          const combinedParts = colConfig.combinedField.map((fieldKey) => {
            const fieldColConfig = (allColumns.value ?? []).find((c) => c.field === fieldKey);
            const fieldValue = (row as any)[fieldKey];

            return renderTableCellValue(
              locale.value,
              t,
              fieldValue,
              fieldColConfig ?? { field: fieldKey, type: 'string' },
              row,
              typeof fieldValue === 'boolean',
            );
          });

          const combinedValue = combinedParts.join(' ').trim();
          metaValue.meta('combinedValue', combinedValue);
          metaValue.meta('textValue', stripHtmlTags(combinedValue));
          metaValue.meta('truncated', undefined);
          value = row[key as keyof T];
        }

        metaValue.meta('rowIndex', rowIndex);
        metaValue.meta('colFieldname', key);
        metaValue.meta('rawvalue', value);
        metaValue.meta('type', type);
        metaValue.meta('isExpanded', expandedColumnsStore.isColumnExpanded(dataKey.value, key));

        const fullValue = value ?? null;
        metaValue.meta(
          'fullValue',
          renderTableCellValue(locale.value, t, fullValue, colConfig, row),
        );

        if (typeof fullValue === 'string' && fullValue.length - 4 > truncateLength) {
          metaValue.meta('truncated', true);
          metaValue.meta('truncatedValue', fullValue.slice(0, truncateLength) + '...');
        } else {
          metaValue.meta('truncatedValue', fullValue);
        }

        metaValue.meta(
          'renderedValue',
          metaValue.meta('truncated') && !metaValue.meta('isExpanded')
            ? metaValue.meta('truncatedValue')
            : metaValue.meta('fullValue'),
        );

        let combinedSearchString = '';
        if (colConfig.combinedField?.length) {
          const searchParts = colConfig.combinedField
            .map((fieldKey) => enrichedItem[fieldKey])
            .map((relatedMeta) =>
              relatedMeta instanceof DataWithMeta
                ? String(relatedMeta.meta('searchString') ?? '')
                : '',
            );
          combinedSearchString = searchParts.filter(Boolean).join(' | ');
        }

        metaValue.meta(
          'searchString',
          combinedSearchString || buildSearchString(t, metaValue.meta(), colConfig.searchKey),
        );

        metaValue.value = metaValue.meta('renderedValue');

        if (metaValue.meta().type === 'enum' && colConfig.sortOrder?.length) {
          const orderIndex = 100 + colConfig.sortOrder.indexOf(metaValue.meta().rawvalue);
          metaValue.value = `${orderIndex} ${metaValue.value}`;
        }

        enrichedItem[key] = metaValue;
      });

      if ((row as any)?._meta) enrichedItem._meta = (row as any)._meta;

      const rawId = (row as any)?.id;
      enrichedItem.__rowKey =
        typeof rawId === 'string' || typeof rawId === 'number' ? rawId : rowIndex;

      return enrichedItem as MetaEnrichedRow<T>;
    });
  }

  /**
   * Returns the DataWithMeta instance for a given row/field.
   */
  function getCell(row: AnyRow, field: string) {
    return row?.[field];
  }

  /**
   * Returns the canonical search string for a cell.
   *
   * Used by quick filtering and global search.
   */
  function getSearchString(row: AnyRow, field: string): string {
    const cell = getCell(row, field);

    const s = cell?.meta?.('searchString');
    if (typeof s === 'string') return s;

    const v = cell?.value ?? cell;
    if (v == null) return '';
    return String(v).toLowerCase();
  }

  /**
   * Returns a value suitable for comparisons and sorting.
   *
   * Guarantees a stable, comparable primitive when possible.
   */
  function getComparableValue(row: AnyRow, field: string): string | number | boolean | Date | null {
    const cell = getCell(row, field);

    const raw = cell?.meta?.('rawvalue');
    if (raw !== undefined) return raw;

    const s = cell?.meta?.('searchString');
    if (typeof s === 'string') return s;

    const v = cell?.value ?? cell;
    if (v == null) return null;

    return v;
  }

  return {
    enrichRows,
    unwrapRow,
    getRowId,
    getSearchString,
    getComparableValue,
  };
}
